import { z } from 'zod';
import {
    type NodeSocketCfg,
    baseNodeCfgSchema,
} from './base-node';
import type { NodeCfg } from '../nodes';
import { locationSchema } from '../../location.deployParam';
import { varConditionSchema } from '../../conditional.zod';
import { workInstructionOutputSchema } from '../../../../../work-instruction-version';

const baseWorkInstruction = z.object({
    kind: z.string(),
    /** keyed by work instruction output id */
    workInstructionOutputs: z.record(workInstructionOutputSchema).default({}),
});

export const processWorkInstruction = baseWorkInstruction.extend({
    kind: z.literal('process'),
    /**
     * NOTE: I used to store the processes linked to steps using step id, but if the step id changed (due to a new version of the work instruction being created) then the work instruction node would break since it would attempt to find the process but wouldnt be able to and error out
     * The same could be said with step number, if you reordered the steps then the step number would change and the node would break
     * So I switched to using step name since that is less likely to change. If the step name does change then the user will have to reconfigure the node, but that is better than it just breaking
     */
    steps: z.array(z.object({
        name: z.string(),
        processes: z.array(z.object({
            name: z.string(),
            id: z.string().uuid(),
            params: z.array(z.object({
                name: z.string(),
                id: z.string().uuid(),
                kind: z.union([ z.literal('string'), z.literal('number'), z.literal('boolean') ]),
            })),
            collectTrigger: varConditionSchema,
            dataElements: z.array(z.object({
                name: z.string(),
                sourceVar: z.string(),
                id: z.string().uuid(),
            })),
        })),
    })),
});

const defaultWorkInstruction = baseWorkInstruction.extend({
    kind: z.literal('default'),
});

const workInstructionDetail = z.discriminatedUnion('kind', [
    processWorkInstruction,
    defaultWorkInstruction,
]);

export const workInstructionNodeCfgSchema = baseNodeCfgSchema.extend({
    kind: z.literal('workInstruction'),
    controlValues: z.object({
        workInstructionDetail,
        location: locationSchema.optional(),
        workInstructionVersionId: z.string().uuid(),
    }),
});
export interface WorkInstructionNodeCfgSchema extends z.infer<typeof workInstructionNodeCfgSchema> {}

export const workInstructionNodeSocketCfg = {
    controlSockets: {
        inputs: () => ({ activate: 'event' }),
        outputs: () => ({
            started: 'event',
            /**
             * this has been renamed to "Done" since it emits when the WI is completed or is finished with skipped steps
             */
            completed: 'event',
            stepUpdate: 'event',
        }),
    },
    dataSockets: {
        outputs: (cfg: NodeCfg<'workInstruction'>) => {
            let ret: {
                totalStepNum?: 'number',
                /**
                 * this represents the step INDEX and is zero based
                 */
                currentStepNum?: 'number',
                workInstructionName?: 'string',
                workInstructionVersion?: 'number',
                [extraOutput: string]: 'string' | 'number' | 'boolean' | undefined,
            } = {
                totalStepNum: 'number',
                currentStepNum: 'number',
                workInstructionName: 'string',
                workInstructionVersion: 'number',
            };
            if (Object.keys(cfg.controlValues.workInstructionDetail.workInstructionOutputs).length) {
                ret = {
                    ...ret,
                    ...Object.entries(cfg.controlValues.workInstructionDetail.workInstructionOutputs).reduce<Record<string, 'string' | 'number' |'boolean'>>((acc, [ outputId, valueDetails ]) => {
                        if (typeof valueDetails.defaultValue === 'number') {
                            acc[outputId] = 'number';
                        } else if (typeof valueDetails.defaultValue === 'boolean') {
                            acc[outputId] = 'boolean';
                        } else if (typeof valueDetails.defaultValue === 'string') {
                            acc[outputId] = 'string';
                        }
                        return acc;
                    }, {}),
                };
            }
            if (cfg.controlValues.workInstructionDetail.kind === 'process') {
                for (const step of cfg.controlValues.workInstructionDetail.steps) {
                    for (const process of step.processes) {
                        for (const param of process.params) {
                            ret[param.id] = param.kind;
                        }
                    }
                }
            }
            return ret;
        },
        inputs: (_cfg: NodeCfg<'workInstruction'>): Partial<Record<'materialId', 'string'>> => {
            const ret: Record<'materialId', 'string'> = {
                materialId: 'string',
            };
            return ret;
        },
    },
} satisfies NodeSocketCfg<WorkInstructionNodeCfgSchema>;
